import React, { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Activity, Calendar, TrendingUp } from 'lucide-react';
import { liveProjects } from '../data/mock';
import { staggerCards } from '../utils/animations';

gsap.registerPlugin(ScrollTrigger);

const LiveProjects = () => {
  const headerRef = useRef(null);
  const projectsRef = useRef(null);

  useEffect(() => {
    // Header animation
    if (headerRef.current) {
      gsap.fromTo(
        headerRef.current.children,
        { opacity: 0, y: 100 },
        { opacity: 1, y: 0, stagger: 0.2, duration: 1, ease: 'power3.out' }
      );
    }

    // Projects animation
    if (projectsRef.current) {
      const cards = projectsRef.current.querySelectorAll('.project-card');
      staggerCards(cards);
    }

    return () => {
      ScrollTrigger.getAll().forEach((trigger) => trigger.kill());
    };
  }, []);

  const getStatusColor = (status) => {
    switch (status) {
      case 'Launched':
        return 'var(--tertiary)';
      case 'Testing Phase':
        return 'var(--primary)';
      case 'In Development':
        return 'var(--secondary)';
      case 'Planning':
        return 'var(--on-surface-variant)';
      default:
        return 'var(--on-surface-variant)';
    }
  };

  return (
    <div className="live-projects-page pt-32">
      {/* Header */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 mb-20">
        <div ref={headerRef} className="text-center">
          <span className="inline-flex items-center gap-2 px-3 py-1 bg-[var(--surface-container-high)] text-[var(--on-surface-variant)] font-body text-xs tracking-code uppercase mb-6 rounded-sm">
            <Activity size={14} className="animate-pulse text-[var(--primary)]" />
            Real-Time Updates
          </span>
          <h1 className="font-headline text-5xl md:text-7xl font-bold tracking-headline uppercase mb-8">
            Live <span className="text-[var(--primary)]">Projects</span>
          </h1>
          <p className="font-body text-[var(--on-surface-variant)] text-xl max-w-3xl mx-auto leading-relaxed">
            Transparency in action. See what we're building right now
            <br />
            and the impact we're creating for our clients.
          </p>
        </div>
      </section>

      {/* Projects Grid */}
      <section ref={projectsRef} className="max-w-7xl mx-auto px-6 lg:px-8 mb-32">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {liveProjects.map((project) => (
            <div
              key={project.id}
              className="project-card group bg-[var(--surface-container-low)] hover:bg-[var(--surface-container-high)] transition-all duration-500 overflow-hidden"
            >
              {/* Top border */}
              <div className="absolute top-0 left-0 w-full h-1 bg-[var(--primary)] transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left" />

              {/* Project Image */}
              <div className="aspect-[16/9] overflow-hidden">
                <img
                  src={project.image}
                  alt={project.name}
                  className="w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-110 transition-all duration-700"
                />
              </div>

              {/* Content */}
              <div className="p-8">
                {/* Status Badge */}
                <div className="flex items-center justify-between mb-4">
                  <span
                    className="px-3 py-1 text-xs font-bold tracking-code uppercase rounded-sm"
                    style={{ 
                      backgroundColor: `${getStatusColor(project.status)}20`,
                      color: getStatusColor(project.status)
                    }}
                  >
                    {project.status}
                  </span>
                  <div className="flex items-center gap-2 text-[var(--on-surface-variant)] text-xs">
                    <Calendar size={14} />
                    {project.startDate}
                  </div>
                </div>

                {/* Project Name */}
                <h3 className="font-headline text-2xl font-bold tracking-tight uppercase mb-2">
                  {project.name}
                </h3>

                {/* Client */}
                <p className="font-body text-[var(--on-surface-variant)] text-sm mb-4">
                  Client: {project.client}
                </p>

                {/* Description */}
                <p className="font-body text-[var(--on-surface-variant)] leading-relaxed mb-6">
                  {project.description}
                </p>

                {/* Progress Bar */}
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-headline text-xs font-bold uppercase tracking-code text-[var(--on-surface-variant)]">
                      Progress
                    </span>
                    <span className="font-headline text-xs font-bold text-[var(--primary)]">
                      {project.progress}%
                    </span>
                  </div>
                  <div className="h-2 bg-[var(--surface-container)] overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-[var(--primary)] to-[var(--primary-container)] transition-all duration-1000"
                      style={{ width: `${project.progress}%` }}
                    />
                  </div>
                </div>

                {/* Tech Stack */}
                <div className="flex flex-wrap gap-2 mb-4">
                  {project.tech.map((tech, idx) => (
                    <span
                      key={idx}
                      className="px-3 py-1 bg-[var(--surface-container)] text-[var(--on-surface-variant)] text-xs font-headline font-bold uppercase tracking-code"
                    >
                      {tech}
                    </span>
                  ))}
                </div>

                {/* Launch Date */}
                <div className="flex items-center gap-2 text-[var(--on-surface-variant)] text-sm pt-4 border-t border-[var(--outline-variant)]/10">
                  <TrendingUp size={16} className="text-[var(--primary)]" />
                  <span className="font-headline font-bold">Expected:</span> {project.expectedLaunch}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-24 bg-[var(--surface-container-lowest)]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { label: "Active Projects", value: liveProjects.filter(p => p.status !== 'Launched').length },
              { label: "Launched This Year", value: "24+" },
              { label: "Client Satisfaction", value: "98%" },
              { label: "On-Time Delivery", value: "95%" }
            ].map((stat, index) => (
              <div
                key={index}
                className="text-center p-8 bg-[var(--surface-container-low)] hover:bg-[var(--surface-container-high)] transition-all duration-300"
              >
                <div className="font-headline text-4xl lg:text-5xl font-bold text-[var(--primary)] mb-2">
                  {stat.value}
                </div>
                <div className="font-body text-xs tracking-code uppercase text-[var(--on-surface-variant)]">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-32">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="font-headline text-4xl md:text-5xl font-bold tracking-headline uppercase mb-8">
            Ready to See Your <span className="text-[var(--primary)]">Project</span> Here?
          </h2>
          <p className="font-body text-[var(--on-surface-variant)] text-lg mb-12">
            Join our portfolio of successful launches
          </p>
          <a
            href="/contact"
            className="magnetic inline-block bg-gradient-to-br from-[var(--primary)] to-[var(--primary-container)] text-[var(--on-primary-container)] px-10 py-4 font-headline font-bold text-sm uppercase tracking-code hover:brightness-110 hover:scale-105 transition-all duration-300 rounded-none"
          >
            Start Your Project
          </a>
        </div>
      </section>
    </div>
  );
};

export default LiveProjects;
